import {useRef, useState, useEffect} from 'react';
import {Stack, Typography, Button, Box} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {NavigationControls} from '../common/NavigationControls';
import {ProgressIndicator} from '../common/ProgressIndicator';
import {ResultCard} from '../common/ResultsCard';
import HealthCategoryNav from '../../../../components/NavigationBar/HealthCategoryNavigationBar';
import {getHealthFunctions} from '../../utils/healthCalculations';
import mainlogo from '../../../../images/black_letters_with_blue_shadow.svg';
import ShareButtons from '../../../../components/buttons/ShareButtons';
import {useLocation, useNavigate} from 'react-router-dom';
import {useCallback} from 'react';

export const CarouselPage = ({
  item,
  handleUpgradeToSeeResults,
  onBackBtnClick,
  isShowAllResultsCard,
  isLastRrsultCard = false,
  isHandleOverlayVisibilty,
  getCategories,
  activeCategoryIndex,
  handleCategorySelect,
  config = {},
  organization = {},
  features = {},
  handleNavigation, // This should now be properly passed from AtplResultsPage
  cardRef,
}) => {
  // Ensure getCategories is a function and returns an array
  const categories = typeof getCategories === 'function' ? getCategories() : [];
  const location = useLocation();
  const navigate = useNavigate();

  // Media queries
  const isSmallScreen = useMediaQuery('(max-width:499px)');
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  // State management
  const [blurAndHide, setBlurAndHide] = useState(false);

  // URL and flow detection
  const isClientResultsUrl = location.pathname.includes('client-results');
  const [isFromDoktor, setIsFromDoktor] = useState(false);
  const [isAtplFlow, setIsAtplFlow] = useState(window.localStorage.getItem('from_atp') === 'true');
  const [isHelloHealthFlow, setIsHelloHealthFlow] = useState(
    window.localStorage.getItem('from_hellohealth') === 'true',
  );

  // Health calculations
  const healthFunctions = getHealthFunctions(item.name);
  const statusColor = healthFunctions.getColor(item.value);
  const statusText = healthFunctions.getStatus(item.value);
  const paramValue = healthFunctions.getScaledValue(item.value);

  // Effects
  useEffect(() => {
    const fromDoktor = window.localStorage.getItem('from_doktor');
    setIsFromDoktor(fromDoktor === 'true');
  }, []);

  // Calculate blur effect
  useEffect(() => {
    const shouldBlur = features.blurCategories?.includes(item.name) && !isShowAllResultsCard && !item.isGameCompleted;
    setBlurAndHide(shouldBlur);
  }, [item, features, isShowAllResultsCard]);

  // Enhanced navigation handler that ensures proper data structure
  const enhancedNavigationHandler = useCallback(() => {
    console.log('Enhanced navigation handler called for:', item.name);
    console.log('Current item:', item);

    // Check if we have a custom handleNavigation function passed from parent
    if (handleNavigation && typeof handleNavigation === 'function') {
      console.log('Using provided handleNavigation function');
      handleNavigation();
      return;
    }

    // Fallback: Navigate directly to analysis with current item
    console.log('Using fallback navigation with current item');

    // Ensure the item has all required properties for the analysis page
    const analysisItem = {
      ...item,
      // Ensure these properties exist for the analysis page
      isGameCompleted: item.isGameCompleted || true,
      userAssessmentID: item.userAssessmentID || localStorage.getItem('session-id') || 'default',
      cardKey: item.cardKey || item.name?.toLowerCase().replace(/\s+/g, ''),
      category: item.category || item.name,
      healthName: item.healthName || item.name,
      fieldValue: item.fieldValue || item.value,
      results: item.results || [], // This should contain the detailed metrics
    };

    console.log('Final analysis item for navigation:', analysisItem);

    navigate('/results/analysis', {
      state: {
        item: analysisItem,
      },
    });
  }, [item, handleNavigation, navigate]);

  // Handle next navigation
  const handleNext = () => {
    const nextUrl = config?.urls?.nextButtonUrl;
    const isExternal = nextUrl?.startsWith('http');

    if (isExternal) {
      window.open(nextUrl, '_blank');
    } else if (nextUrl) {
      navigate(nextUrl);
    }
  };

  const handleCategoryNavigation = useCallback(
    index => {
      console.log('Category navigation triggered:', index);
      if (handleCategorySelect) {
        handleCategorySelect(index);
      }
    },
    [handleCategorySelect],
  );

  if (isLargeScreen) {
    return (
      <LargeScreenCarousel
        item={item}
        handleUpgradeToSeeResults={handleUpgradeToSeeResults}
        onBackBtnClick={onBackBtnClick}
        isShowAllResultsCard={isShowAllResultsCard}
        isLastRrsultCard={isLastRrsultCard}
        isHandleOverlayVisibilty={isHandleOverlayVisibilty}
        getCategories={getCategories}
        activeCategoryIndex={activeCategoryIndex}
        handleCategorySelect={handleCategorySelect}
        config={config}
        organization={organization}
        features={features}
        handleNavigation={enhancedNavigationHandler} // Use enhanced handler
        cardRef={cardRef}
        blurAndHide={blurAndHide}
        statusColor={statusColor}
        statusText={statusText}
        paramValue={paramValue}
        isSmallScreen={isSmallScreen}
        isGalaxyFold={isGalaxyFold}
      />
    );
  }

  return (
    <Stack
      display="flex"
      flexDirection="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{width: 1}}
      className="results-page-wrapper"
    >
      <Stack
        className="result-page-card"
        padding={isGalaxyFold ? 1 : 3}
        paddingTop="1px"
        sx={{backgroundColor: '#fff'}}
        ref={cardRef}
      >
        {/* Navigation Controls */}
        <NavigationControls
          onBack={!isFromDoktor ? onBackBtnClick : undefined}
          onNext={handleNext}
          showBack={!isFromDoktor}
          showNext={true}
          config={config}
        />

        <Stack>
          {/* Top Header Starts */}
          <Stack
            className="top-header"
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
            sx={{
              mt: 2,
              position: 'relative',
              bottom: isAtplFlow || isHelloHealthFlow ? '15px' : '0px',
            }}
          >
            <Stack>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{fontSize: isGalaxyFold ? '16px' : isSmallScreen ? '18px' : '23px'}}
              >
                {item.name}
              </Typography>
              <Typography
                variant="p"
                fontWeight="600"
                sx={{
                  color: statusColor,
                  fontSize: isGalaxyFold ? '12px' : isSmallScreen ? '14px' : '15px',
                  filter: blurAndHide ? 'blur(6px)' : 'blur(0px)',
                }}
              >
                {statusText}
              </Typography>
            </Stack>

            <ProgressIndicator
              value={paramValue}
              size={isGalaxyFold ? 60 : isSmallScreen ? 64 : 80}
              thickness={isGalaxyFold ? 6 : isSmallScreen ? 5 : 8}
              color={statusColor}
              blurred={blurAndHide}
              showValue={true}
            />
          </Stack>
          {/* Top Header Ends */}

          {/* Results Card */}
          <Stack
            className="result-component card-gradient"
            ref={cardRef}
            sx={{
              mt: 2,
              bottom:
                isAtplFlow || isHelloHealthFlow
                  ? '50px'
                  : config?.resultPageSlider?.isResultPageSliderVisible
                    ? '30px'
                    : '0px',
            }}
          >
            {item.results?.map((result, index) => (
              <ResultCard
                key={index}
                metric={result}
                config={config}
                isSmallScreen={isSmallScreen}
                isGalaxyFold={isGalaxyFold}
                blurAndHide={blurAndHide}
              />
            ))}

            {/* Logo Section */}
            <Box
              className="result-logo"
              sx={{
                p: 2,
                textAlign: 'center',
                marginBottom: organization.type !== 'DEFAULT' && isSmallScreen ? '1px' : '18px',
              }}
            >
              {organization.type === 'ATP' ? (
                <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                  <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                </a>
              ) : (
                <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
              )}
              {organization.type !== 'DEFAULT' && (
                <Typography
                  sx={{
                    fontSize: isGalaxyFold ? '8px' : '10px',
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Your health scan was powered by
                  <img src={mainlogo} className="custom-main-logo" alt="Powered by" width={50} />
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Navigation */}
          {config?.resultPageSlider?.isResultPageSliderVisible && categories.length > 0 && (
            <Box
              sx={{
                mt: 0,
                position: 'relative',
                bottom:
                  isAtplFlow || isHelloHealthFlow
                    ? '50px'
                    : config?.resultPageSlider?.isResultPageSliderVisible
                      ? '30px'
                      : '0px',
              }}
            >
              <HealthCategoryNav
                categories={categories}
                activeIndex={activeCategoryIndex}
                onSelectCategory={handleCategoryNavigation} // Use the wrapped handler
                isLargeScreen={false}
                clientBaseNavCss={config.resultPageSlider}
              />
            </Box>
          )}

          {/* Bottom Info Section */}
          <Stack className="bottom-info">
            <Typography
              color="black"
              marginTop="0px"
              variant="p"
              component="p"
              fontWeight="400"
              sx={{
                fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '12px',
                position:
                  isAtplFlow || isHelloHealthFlow || config?.resultPageSlider?.isResultPageSliderVisible
                    ? 'relative'
                    : null,
                bottom:
                  isAtplFlow || isHelloHealthFlow
                    ? '50px'
                    : config?.resultPageSlider?.isResultPageSliderVisible
                      ? '30px'
                      : '0px',
              }}
            >
              These numbers are only estimates and are not a substitute for the judgment of a healthcare professional.
              They are intended to improve awareness and general wellness.
            </Typography>

            <Box
              marginTop="10px"
              sx={{
                position:
                  isAtplFlow || isHelloHealthFlow || config?.resultPageSlider?.isResultPageSliderVisible
                    ? 'relative'
                    : null,
                bottom:
                  isAtplFlow || isHelloHealthFlow
                    ? '50px'
                    : config?.resultPageSlider?.isResultPageSliderVisible
                      ? '30px'
                      : '0px',
              }}
            >
              {blurAndHide ? (
                <Stack
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.69)',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '17px',
                      textAlign: 'center',
                      padding: '2px 8px 10px 8px',
                    }}
                  >
                    $2.99 per week
                  </Typography>
                  <Button
                    fullWidth
                    className={'btn-shadow'}
                    sx={{
                      height: isSmallScreen ? '50px' : '60px',
                      borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
                      marginBottom: isSmallScreen ? '6px' : '10px',
                      background: config.button?.background,
                      maxWidth: '400px',
                      textAlign: 'center',
                      color: '#fff',
                      '&:hover': {
                        color: 'black',
                      },
                    }}
                    onClick={handleUpgradeToSeeResults}
                  >
                    <Typography
                      className="btn-txt"
                      variant="p"
                      component="p"
                      fontWeight="600"
                      sx={{
                        fontSize: isSmallScreen ? '16px' : '20px',
                      }}
                    >
                      🚀 Upgrade to See Results
                    </Typography>
                  </Button>
                  <Typography
                    sx={{
                      color: '#35C6C9',
                      fontSize: isSmallScreen ? '18px' : '20px',
                      fontWeight: 600,
                      lineHeight: '25px',
                      textAlign: 'center',
                      padding: '10px 0px',
                    }}
                  >
                    I'd Rather Invite Friends to View My Results... 👯‍♀️
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Button
                    fullWidth
                    onClick={enhancedNavigationHandler} // Use enhanced navigation handler
                    sx={{
                      mt: 0,
                      height: isSmallScreen ? '50px' : '60px',
                      borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
                      background: config.button?.background,
                      color: '#fff',
                      fontSize: isSmallScreen ? '16px' : '18px',
                      fontWeight: 600,
                      marginBottom: isSmallScreen ? '6px' : '10px',
                      maxWidth: '400px',
                      '&:hover': {
                        color: 'black',
                      },
                    }}
                  >
                    👀 Find Products That Can Help
                  </Button>
                  <ShareButtons cardRef={cardRef} item={item} socialButtonsCss={config?.socialButtons} />
                </>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const LargeScreenCarousel = ({
  item,
  getCategories,
  config = {},
  organization = {},
  cardRef,
  blurAndHide,
  handleNavigation, // Add this prop for large screen
}) => {
  const categories = typeof getCategories === 'function' ? getCategories() : [];

  return (
    <Stack
      display="flex"
      flexDirection="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{width: 1}}
      marginTop={organization.type === 'DOKTOR_CONNECT' ? '24px' : undefined}
      className="results-page-wrapper"
      ref={cardRef}
    >
      <Stack className="result-page-card" padding={2} ref={cardRef}>
        <Stack
          display="flex"
          flexDirection="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{width: 1}}
          className="results-page-wrapper"
        >
          <Stack className="result-page-card large-card" padding={3} sx={{backgroundColor: 'transparent'}}>
            <Stack className="large-result-component">
              <Stack className="result-component card-gradient large">
                {/* Results List */}
                <Stack pt={5}>
                  {item.results?.map((result, index) => (
                    <ResultCard
                      key={index}
                      metric={result}
                      config={config}
                      isSmallScreen={false}
                      isGalaxyFold={false}
                      blurAndHide={blurAndHide}
                    />
                  ))}
                </Stack>

                {/* Logo Section */}
                <Box className="result-logo" sx={{marginBottom: '18px'}}>
                  {organization.type === 'ATP' ? (
                    <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                      <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                    </a>
                  ) : (
                    <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                  )}
                  {organization.type !== 'DEFAULT' && (
                    <Typography sx={{fontSize: '14px'}}>
                      Your health scan was powered by
                      <img src={mainlogo} className="custom-main-logo-for-md" alt="Powered by" />
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
